import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'localization';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import {
  ConfirmDialogAtom,
  confirmDialogAtom,
  SpinnerAtom,
  useApiClientsOrNull,
  useUserIdentityContext,
} from 'design-system/my-lib';
import './IdentityDetailPage.scss';

type GremlinProperty = string | string[] | undefined | null;

interface RawOrganization {
  id: string;
  label?: string;
  name?: GremlinProperty;
  description?: GremlinProperty;
  type?: GremlinProperty;
  imageId?: GremlinProperty;
}

interface RawUser {
  id: string;
  label?: string;
  name?: GremlinProperty;
  description?: GremlinProperty;
  type?: string;
  imageId?: GremlinProperty;
  organization?: RawOrganization[];
}

interface RawIdentity {
  id: string;
  label?: string;
  name?: GremlinProperty;
  description?: GremlinProperty;
  type?: GremlinProperty;
  defaultUserId?: GremlinProperty;
  imageId?: GremlinProperty;
}

interface IdentityWithUsersData {
  identity: RawIdentity;
  users: RawUser[];
}

interface NormalizedUser {
  id: string;
  name: string;
  organizationName: string;
  organizationId: string | null;
}

interface NormalizedOrg {
  id: string;
  name: string;
}

interface NormalizedIdentity {
  id: string;
  name: string;
  email: string;
  displayName: string;
}

type AdminCheckState = 'idle' | 'checking' | 'admin' | 'notAdmin' | 'error';

const firstValue = (prop: GremlinProperty): string => {
  if (Array.isArray(prop)) return prop[0] ?? '';
  if (typeof prop === 'string') return prop;
  return '';
};

const normalizeIdentity = (raw: RawIdentity): NormalizedIdentity => {
  const name = firstValue(raw.name);
  const description = firstValue(raw.description);
  return {
    id: raw.id,
    name,
    email: name,
    displayName: description || name,
  };
};

const normalizeUsers = (raw: RawUser[]): NormalizedUser[] =>
  raw.map((u) => {
    const org = u.organization?.[0];
    return {
      id: u.id,
      name: firstValue(u.name) || u.id,
      organizationName: org ? firstValue(org.name) || org.id : '',
      organizationId: org?.id ?? null,
    };
  });

const deriveOrgs = (raw: RawUser[]): NormalizedOrg[] => {
  const seen = new Map<string, NormalizedOrg>();
  raw.forEach((u) => {
    (u.organization ?? []).forEach((org) => {
      if (!org?.id || seen.has(org.id)) return;
      seen.set(org.id, {
        id: org.id,
        name: firstValue(org.name) || org.id,
      });
    });
  });
  return Array.from(seen.values());
};

const IdentityDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClients = useApiClientsOrNull();
  const { identityId: currentIdentityId } = useUserIdentityContext();
  const { identityId: routeIdentityId } = useParams<{ identityId: string }>();

  const [identity, setIdentity] = useState<NormalizedIdentity | null>(null);
  const [users, setUsers] = useState<NormalizedUser[]>([]);
  const [orgs, setOrgs] = useState<NormalizedOrg[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [adminState, setAdminState] = useState<AdminCheckState>('idle');
  const [toggling, setToggling] = useState<boolean>(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const targetId = routeIdentityId ?? '';
  const isSelf = Boolean(
    currentIdentityId && targetId && currentIdentityId === targetId
  );

  // Fetch identity detail.
  useEffect(() => {
    if (!apiClients?.graphClient || !targetId) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setFetchError(null);
    // Every failure is caught inside and becomes the not-found or fetch-error state.
    void (async () => {
      try {
        // ApiClient.get resolves to the response body: `{ identity: { identity, users } }`.
        const res = await apiClients.graphClient.get<{
          identity?: IdentityWithUsersData;
        }>(`/identities/${targetId}/users`);
        if (cancelled) return;
        const payload = res?.identity;
        if (!payload?.identity) {
          setNotFound(true);
          setIdentity(null);
          setUsers([]);
          setOrgs([]);
          return;
        }
        setIdentity(normalizeIdentity(payload.identity));
        setUsers(normalizeUsers(payload.users ?? []));
        setOrgs(deriveOrgs(payload.users ?? []));
      } catch (err) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) {
          setNotFound(true);
        } else {
          setFetchError(t('admin.identities.detail.fetchFailed'));
        }
        setIdentity(null);
        setUsers([]);
        setOrgs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClients, targetId, t]);

  // Check platform admin status for the target identity.
  const runAdminCheck = useCallback(async () => {
    if (!apiClients?.permissionClient || !targetId) return;
    setAdminState('checking');
    try {
      const res = await apiClients.permissionClient.get<{ isAdmin: boolean }>(
        `/admin/check?identityId=${encodeURIComponent(targetId)}`
      );
      setAdminState(res?.isAdmin === true ? 'admin' : 'notAdmin');
    } catch (err) {
      // A rejected check (503 while SpiceDB is unreachable, any other failure)
      // is "could not check", never "not an admin": no Grant is offered for an
      // identity that may already hold admin.
      console.error('[IdentityDetailPage] admin check failed', err);
      setAdminState('error');
    }
  }, [apiClients, targetId]);

  useEffect(() => {
    runAdminCheck();
  }, [runAdminCheck]);

  const doGrantOrRevoke = useCallback(
    async (grant: boolean) => {
      if (!apiClients?.organizationClient || !identity?.email) return;
      setToggling(true);
      setToggleError(null);
      const previous = adminState;
      // Optimistic update for non-self toggles only; self-revoke waits for
      // backend confirmation before redirecting so a failure leaves the UI
      // in a recoverable state.
      if (!isSelf) {
        setAdminState(grant ? 'admin' : 'notAdmin');
      }
      try {
        await apiClients.organizationClient.post('/manage-platform-admin', {
          email: identity.email,
          grant,
        });
        if (isSelf && !grant) {
          // Self-revoke: hard-redirect + reload. Clears cached guard state,
          // Redux selectors, and any in-flight API clients that still trust
          // the prior grant. SPA nav is not sufficient.
          globalThis.location.assign('/');
          globalThis.location.reload();
          return;
        }
        // Re-check admin state from source of truth after non-self toggle.
        await runAdminCheck();
      } catch (err) {
        console.error('[IdentityDetailPage] manage-platform-admin failed', err);
        if (!isSelf) setAdminState(previous);
        setToggleError(
          grant
            ? t('admin.identities.detail.admin.errors.grantFailed')
            : t('admin.identities.detail.admin.errors.revokeFailed')
        );
      } finally {
        setToggling(false);
      }
    },
    [apiClients, identity, adminState, isSelf, runAdminCheck, t]
  );

  const handleGrantClick = useCallback(() => {
    doGrantOrRevoke(true);
  }, [doGrantOrRevoke]);

  const handleRevokeClick = useCallback(() => {
    if (isSelf) {
      confirmDialogAtom({
        header: t('admin.identities.detail.admin.selfRevoke.title'),
        message: t('admin.identities.detail.admin.selfRevoke.message'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: t('admin.identities.detail.admin.selfRevoke.confirm'),
        rejectLabel: t('admin.identities.detail.admin.selfRevoke.cancel'),
        variant: 'danger',
        accept: () => doGrantOrRevoke(false),
      });
      return;
    }
    doGrantOrRevoke(false);
  }, [isSelf, doGrantOrRevoke, t]);

  const handleRetryAdminCheck = useCallback(() => {
    void runAdminCheck();
  }, [runAdminCheck]);

  const handleBack = useCallback(() => {
    navigate('/admin/metasphere/identities');
  }, [navigate]);

  const userRows = useMemo(() => users, [users]);
  const orgRows = useMemo(() => orgs, [orgs]);

  const statusMessage = useMemo(() => {
    switch (adminState) {
      case 'checking':
        return t('admin.identities.detail.admin.status.checking');
      case 'admin':
        return t('admin.identities.detail.admin.status.admin');
      case 'notAdmin':
        return t('admin.identities.detail.admin.status.notAdmin');
      case 'error':
        return t('admin.identities.detail.admin.status.error');
      default:
        return '';
    }
  }, [adminState, t]);

  if (loading) {
    return (
      <div
        className="identity-detail-page identity-detail-page--loading"
        role="status"
        aria-live="polite"
        data-testid="identity-detail-page"
      >
        <SpinnerAtom size="large" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        className="identity-detail-page identity-detail-page--not-found"
        data-testid="identity-detail-page"
      >
        <Message
          severity="warn"
          text={t('admin.identities.detail.notFound')}
        />
        <Button
          className="identity-detail-page__back"
          icon="pi pi-arrow-left"
          label={t('admin.identities.detail.backToList')}
          onClick={handleBack}
          text
          data-testid="identity-detail-back"
        />
      </div>
    );
  }

  if (fetchError || !identity) {
    return (
      <div
        className="identity-detail-page identity-detail-page--error"
        data-testid="identity-detail-page"
      >
        <Message
          severity="error"
          text={fetchError || t('admin.identities.detail.fetchFailed')}
        />
        <Button
          className="identity-detail-page__back"
          icon="pi pi-arrow-left"
          label={t('admin.identities.detail.backToList')}
          onClick={handleBack}
          text
          data-testid="identity-detail-back"
        />
      </div>
    );
  }

  const canToggle = Boolean(identity.email);
  const grantDisabled = !canToggle || toggling || adminState === 'checking';
  const showGrant = adminState === 'notAdmin';
  const showRevoke = adminState === 'admin';
  const showRetry = adminState === 'error';

  return (
    <div
      className="identity-detail-page"
      data-testid="identity-detail-page"
    >
      <ConfirmDialogAtom />

      <header className="identity-detail-page__header">
        <Button
          className="identity-detail-page__back"
          icon="pi pi-arrow-left"
          label={t('admin.identities.detail.backToList')}
          onClick={handleBack}
          text
          data-testid="identity-detail-back"
        />
        <h1 className="identity-detail-page__title">
          {t('admin.identities.detail.title')}
        </h1>
      </header>

      <section
        className="identity-detail-page__card"
        aria-label={t('admin.identities.detail.title')}
      >
        <dl className="identity-detail-page__card-grid">
          <div className="identity-detail-page__card-row">
            <dt>{t('admin.identities.detail.card.id')}</dt>
            <dd data-testid="identity-detail-id">{identity.id}</dd>
          </div>
          <div className="identity-detail-page__card-row">
            <dt>{t('admin.identities.detail.card.email')}</dt>
            <dd data-testid="identity-detail-email">{identity.email || '—'}</dd>
          </div>
          <div className="identity-detail-page__card-row">
            <dt>{t('admin.identities.detail.card.displayName')}</dt>
            <dd data-testid="identity-detail-display-name">
              {identity.displayName || '—'}
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="identity-detail-page__admin"
        aria-label={t('admin.identities.detail.admin.title')}
      >
        <h2 className="identity-detail-page__section-title">
          {t('admin.identities.detail.admin.title')}
        </h2>
        <p
          className={`identity-detail-page__admin-status identity-detail-page__admin-status--${adminState}`}
          data-testid="identity-detail-admin-status"
        >
          {statusMessage}
        </p>
        {!canToggle && (
          <Message
            severity="info"
            text={t('admin.identities.detail.admin.errors.noEmail')}
          />
        )}
        {toggleError && (
          <Message severity="error" text={toggleError} />
        )}
        <div className="identity-detail-page__admin-actions">
          {showRetry && (
            <Button
              label={t('admin.identities.detail.admin.retry')}
              icon="pi pi-refresh"
              onClick={handleRetryAdminCheck}
              outlined
              data-testid="identity-detail-admin-retry"
            />
          )}
          {showGrant && (
            <Button
              label={t('admin.identities.detail.admin.grant')}
              icon="pi pi-shield"
              onClick={handleGrantClick}
              disabled={grantDisabled}
              data-testid="identity-detail-grant"
            />
          )}
          {showRevoke && (
            <Button
              label={t('admin.identities.detail.admin.revoke')}
              icon="pi pi-ban"
              severity="danger"
              onClick={handleRevokeClick}
              disabled={grantDisabled}
              data-testid="identity-detail-revoke"
            />
          )}
        </div>
      </section>

      <section
        className="identity-detail-page__users"
        aria-label={t('admin.identities.detail.users.title')}
      >
        <h2 className="identity-detail-page__section-title">
          {t('admin.identities.detail.users.title')}
        </h2>
        <DataTable
          value={userRows}
          emptyMessage={t('admin.identities.detail.users.empty')}
          dataKey="id"
          stripedRows
          className="identity-detail-page__table"
          data-testid="identity-detail-users-table"
        >
          <Column
            field="name"
            header={t('admin.identities.detail.users.columns.name')}
          />
          <Column
            field="organizationName"
            header={t('admin.identities.detail.users.columns.organization')}
          />
        </DataTable>
      </section>

      <section
        className="identity-detail-page__orgs"
        aria-label={t('admin.identities.detail.orgs.title')}
      >
        <h2 className="identity-detail-page__section-title">
          {t('admin.identities.detail.orgs.title')}
        </h2>
        {orgRows.length === 0 ? (
          <p
            className="identity-detail-page__empty"
            data-testid="identity-detail-orgs-empty"
          >
            {t('admin.identities.detail.orgs.empty')}
          </p>
        ) : (
          <ul
            className="identity-detail-page__org-list"
            data-testid="identity-detail-orgs-list"
          >
            {orgRows.map((o) => (
              <li key={o.id}>{o.name}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default IdentityDetailPage;
